/**
 * Notifications — full history of document-generation results and other
 * background activity, mirroring Noteble's History ▸ Notifications tab. The
 * bell panel only ever shows the single latest item, so this is where a user
 * finds anything further back. Reachable from the account menu.
 *
 * This page OWNS its list. The provider fetches one small page for the bell
 * and never paginates, so a history that reads from context can only ever show
 * what the bell already had. Here we hold `items` + `nextCursor` ourselves and
 * hand them to the shared <NotificationHistory> through its `items` / footer
 * props, which keeps the row rendering shared with Noteble while the paging
 * lives where the data does.
 *
 * The consequence to keep in mind: each notification now exists in two places
 * — this array and the provider's — so every dismissal has to touch both or
 * the bell badge drifts from the visible list.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotificationHistory, useNotifications } from '@puglieseweb/shared-react-notifications';
import type { NotificationItem } from '@puglieseweb/shared-react-notifications';
import { LoadingState } from '@systemdox/ui';
import { notificationActionUrl, recordToNotificationItem } from '@/services/notificationAdapter';
import { notificationsApi } from '@/services/notificationsApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('NotificationsPage');

/** Rows per request. Matches the server default; explicit beats implicit. */
const PAGE_SIZE = 25;

/**
 * Follow-up dismiss-all sweeps before we stop and tell the user.
 *
 * The server bounds how far it walks in one call, so a big backlog needs
 * several. Bounded because "keep going until done" against a partition we do
 * not control is how a click becomes an infinite loop.
 */
const MAX_DISMISS_ALL_SWEEPS = 3;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { refreshJobs, dismissNotification, dismissAllNotifications } = useNotifications();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The cursor as a ref as well as state: loadMore reads it without being
  // re-created on every page, and comparing against it on response is what
  // makes a superseded request (a reload landing mid-fetch) discardable.
  const cursorRef = useRef<string | null>(null);
  const isLoadingMoreRef = useRef(false);

  const loadFirstPage = useCallback(async () => {
    cursorRef.current = null;
    try {
      const page = await notificationsApi.list({ limit: PAGE_SIZE, includeDismissed: true });
      setItems(page.notifications.map(recordToNotificationItem));
      setNextCursor(page.nextCursor);
      cursorRef.current = page.nextCursor;
      setError(null);
    } catch (err) {
      log.error('Failed to load notifications', err as Error);
      setError('Could not load notifications. Try again.');
    } finally {
      setHasLoaded(true);
    }
  }, []);

  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    // No cursor means the history is exhausted; the in-flight guard stops a
    // double click from fetching the same page twice.
    if (!cursor || isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const page = await notificationsApi.list({ limit: PAGE_SIZE, cursor, includeDismissed: true });
      // Superseded while in flight — applying this would splice a page from an
      // abandoned walk into the current list.
      if (cursorRef.current !== cursor) return;
      setItems((prev) => {
        // Dedupe on the shared id: cheap insurance against a page boundary
        // repeating a row, which would otherwise throw a duplicate-key warning
        // and render the row twice.
        const seen = new Set(prev.map((n) => n.id));
        const incoming = page.notifications
          .map(recordToNotificationItem)
          .filter((n) => !seen.has(n.id));
        return [...prev, ...incoming];
      });
      setNextCursor(page.nextCursor);
      cursorRef.current = page.nextCursor;
      setError(null);
    } catch (err) {
      // Deliberately leaves `items` alone: a failed "load more" must not throw
      // away the pages the user already has.
      log.error('Failed to load more notifications', err as Error);
      setError('Could not load more notifications. Try again.');
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, []);

  // Reached by client-side navigation, so nothing else refetches. refreshJobs()
  // re-reads in-flight jobs (still context-owned, and rendered above the list);
  // loadFirstPage() reads the history this page owns.
  useEffect(() => {
    void refreshJobs();
    void loadFirstPage();
  }, [refreshJobs, loadFirstPage]);

  /**
   * Dismiss one row.
   *
   * Context first — that is the call that reaches the server and updates the
   * bell — then prune our own copy. Both lists key on the same `job-` prefixed
   * id, so this is a plain equality filter with no id translation.
   */
  const handleDismiss = useCallback(
    async (id: string) => {
      await dismissNotification(id);
      setItems((prev) => prev.filter((n) => n.id !== id));
    },
    [dismissNotification],
  );

  /**
   * Clear everything.
   *
   * The context dismisser clears the bell and issues the first sweep, but its
   * return type cannot carry the server's `complete` flag, so we follow up with
   * bounded direct sweeps until the server says it is done. In the common case
   * that is one extra request which returns `dismissed: 0, complete: true` and
   * confirms the drain finished. If it never does, say so rather than showing a
   * cleared page that repopulates on the next visit.
   */
  const handleDismissAll = useCallback(async () => {
    setItems([]);
    setNextCursor(null);
    cursorRef.current = null;
    try {
      await dismissAllNotifications();
      let complete = false;
      for (let sweep = 0; sweep < MAX_DISMISS_ALL_SWEEPS && !complete; sweep += 1) {
        ({ complete } = await notificationsApi.dismissAll());
      }
      setError(complete ? null : 'Some notifications could not be cleared yet. Try again.');
    } catch (err) {
      log.error('Failed to clear notifications', err as Error);
      setError('Could not clear notifications. Try again.');
    }
    // Re-read regardless: whatever survived the sweep belongs back on screen.
    await loadFirstPage();
  }, [dismissAllNotifications, loadFirstPage]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Notifications</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Complete history of AI-generated documents and other background activity.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          {!hasLoaded ? (
            // NotificationHistory has no loading concept: rendering it before
            // the first fetch settles flashes "No notifications" for a paint.
            <LoadingState size="lg" label="Loading notifications…" />
          ) : (
            <NotificationHistory
              items={items}
              onDismiss={handleDismiss}
              onDismissAll={handleDismissAll}
              onNotificationClick={(notification) => {
                // Durable notifications carry their link in job.meta.
                const url = notificationActionUrl(notification);
                if (url) navigate(url);
              }}
              footer={
                (error || nextCursor) && (
                  <div className="flex flex-col items-center gap-2 px-4 py-3">
                    {error && (
                      <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                        {error}
                      </p>
                    )}
                    {/* Gated on the cursor, NEVER on page length: a filtered
                        page is legitimately short with more still to come. */}
                    {nextCursor && (
                      <button
                        type="button"
                        onClick={() => void loadMore()}
                        disabled={isLoadingMore}
                        className="text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 disabled:opacity-50 transition-colors"
                      >
                        {isLoadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    )}
                  </div>
                )
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
